import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslate } from '@bynder/localization';
import { Button, InputField, List } from '@bynder/design-system';
import { IconArrowRight, IconCancel, IconCheck } from '@bynder/icons';
import componentCollectionService from 'packages/services/componentCollectionService';
import { EnrichedComponentCollection } from './types';

interface EditableCollectionNameProps {
    collection: EnrichedComponentCollection;
    onClick: (collection: EnrichedComponentCollection) => void;
    onDoubleClick: (collection: EnrichedComponentCollection) => void;
    onUpdate: (id: number, name: string) => void;
    subtext: string;
}

const EditableCollectionName = ({
    collection,
    onClick,
    onDoubleClick,
    onUpdate,
    subtext,
}: EditableCollectionNameProps) => {
    const { translate } = useTranslate();
    const [isEditing, setIsEditing] = useState(false);
    const [editingName, setEditingName] = useState('');

    const inputValidator = useMemo(() => {
        return {
            maxLength: editingName.trim().length > 50,
            emptyLength: editingName.trim().length === 0,
        };
    }, [editingName]);

    const isInvalid = Object.values(inputValidator).some((invalid) => invalid);

    const validationHelperText = useMemo(() => {
        if (inputValidator.emptyLength) {
            return translate('components.collection.edit.validation.nameRequired');
        }

        if (inputValidator.maxLength) {
            return translate('components.collection.edit.validation.nameLength');
        }

        return '';
    }, [inputValidator, translate]);

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing) {
            inputRef.current?.focus();
        }
    }, [isEditing]);

    const handleDoubleClick = () => {
        setIsEditing(true);
        setEditingName(collection.name);
        onDoubleClick(collection);
    };

    const handleSave = async () => {
        if (!editingName.trim() || isInvalid) {
            setIsEditing(false);

            return;
        }

        const res = await componentCollectionService.editComponentCollection(collection.id, editingName);

        if (res.status === 200) {
            onUpdate(collection.id, editingName);
        }

        setIsEditing(false);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditingName('');
    };

    if (isEditing) {
        return (
            <List.Item
                rightElements={
                    <div role="presentation" onMouseDown={(e) => e.preventDefault()}>
                        <Button
                            variant="secondary"
                            onClick={handleCancel}
                            title={translate('components.collection.edit.cancel')}
                            icon={<IconCancel />}
                        />
                        <Button
                            variant="primary"
                            onClick={handleSave}
                            title={translate('components.collection.edit.save')}
                            isDisabled={!editingName.trim() || editingName.trim() === collection.name || isInvalid}
                            icon={<IconCheck />}
                        />
                    </div>
                }
            >
                <InputField
                    inputRef={inputRef}
                    label=""
                    value={editingName}
                    onChange={setEditingName}
                    onBlur={!isInvalid ? handleSave : handleCancel}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isInvalid) {
                            handleSave();
                        } else if (e.key === 'Escape') {
                            handleCancel();
                        }
                    }}
                    isInvalid={isInvalid}
                    helperText={validationHelperText}
                    type="text"
                />
            </List.Item>
        );
    }

    return (
        <List.Item subtext={subtext} onClick={() => onClick(collection)} rightElements={<IconArrowRight />}>
            <span onDoubleClick={handleDoubleClick} style={{ width: '100%', display: 'inline-block' }}>
                {collection.name}
            </span>
        </List.Item>
    );
};

export default EditableCollectionName;
